import React, { useEffect, useState, useMemo } from 'react';
import { X, Loader2, AlertTriangle, Check, UserMinus, Clock, Wallet, TrendingDown, Calendar as CalIcon } from 'lucide-react';
import { EVENT_TYPES, getHourBalance, jsDowToBackend } from '../services/api';
import {
  sumManualOffsets, formatBalance, eligibleForBalance, formatNoScheduleBalance, NO_SCHEDULE_ADMIN_HINT,
} from '../lib/hourBalance';
import { loadWorkerHourBalance, invalidateWorkerHourBalance } from '../hooks/useWorkerHourBalance';
import { findActiveEntry } from '../lib/timeline';
import {
  DEFAULT_TIME_ZONE, timeZoneForWorker, timeZoneLabel, dayKeyInZone, formatHmInZone,
  dayKeysBetween, dowOfDayKey, zonedDateTimeToInstant,
} from '../lib/workerTime';

// Modal de confirmación al aprobar una ausencia justificada (SICK_LEAVE o
// ABSENCE). Reemplaza a los dos botones inline "No recuperable / Recuperable"
// porque:
//   · El cálculo multi-día NO era obvio en los botones (si la ausencia
//     abarca varios días, los días intermedios también cuentan).
//   · El admin necesita ver cómo queda la bolsa ANTES de decidir, no
//     después de pulsar.
//
// El cálculo de minutos:
//   · Día completo (SICK_LEAVE): suma la jornada COMPLETA de cada día
//     entre el START y el END (inclusive). Si la jornada es de turno
//     partido, suman mañana + tarde.
//   · Por horas (ABSENCE): intersecta el rango [START, END] con la
//     jornada de cada día cubierto. Día único = clamp normal. Multi-día
//     = primer día desde START hasta fin jornada, días intermedios
//     enteros, último día desde inicio jornada hasta END.

// `dayKey` = 'YYYY-MM-DD'. Las horas del slot son hora de reloj del
// TRABAJADOR, así que el intervalo se construye en su zona (`tz`).
const slotIntervalsForDay = (slot, dayKey, tz = DEFAULT_TIME_ZONE) => {
  if (!slot) return [];
  const buildInterval = (h1, h2) => {
    if (!h1 || !h2) return null;
    const trim = (s) => String(s).slice(0, 5);
    const s = zonedDateTimeToInstant(dayKey, trim(h1), tz).getTime();
    const e = zonedDateTimeToInstant(dayKey, trim(h2), tz).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e)) return null;
    if (e <= s) return null;
    return [s, e];
  };
  const out = [];
  const i1 = buildInterval(slot.in1, slot.out1);
  const i2 = buildInterval(slot.in2, slot.out2);
  if (i1) out.push(i1);
  if (i2) out.push(i2);
  return out;
};

const intersectMinutes = (intervals, winStart, winEnd) => {
  let total = 0;
  for (const [s, e] of intervals) {
    const a = Math.max(s, winStart);
    const b = Math.min(e, winEnd);
    if (b > a) total += Math.floor((b - a) / 60000);
  }
  return total;
};

// Resultado:
//   {
//     minutes: number,            // los que se descontarían si recoverable=true
//     mode: 'jornada' | 'rango-exacto' | 'sin-jornada',
//     // jornada      → calculado intersectando con los slots del trabajador (caso ideal).
//     // rango-exacto → fallback para "por horas" SIN jornada: usamos endTs-startTs
//     //                directo porque "por horas" es un tiempo real medido entre
//     //                los dos timestamps; no necesitamos jornada para saberlo.
//     // sin-jornada  → "día completo" sin jornada: NO podemos inventar duración,
//     //                devolvemos 0 y la UI bloquea el botón de descontar.
//   }
//
// Acepta `scheduleHistory` opcional: array de ScheduleVersion con
// `{validFrom, validTo, assignSlots}`. Si está presente, para cada día
// iterado buscamos la VERSIÓN vigente en esa fecha y usamos sus slots
// (esto cubre el caso "el trabajador cambió de horario el 1-jul: hasta
// junio 38h/sem, desde julio 40h/sem"). Si no hay versión vigente para
// alguna fecha, cae al `workhours` actual del trabajador como fallback.
//
// `timeZone`: zona del trabajador (timeZoneForWorker). Los días que cubre el
// periodo y las horas de su jornada se resuelven en esa zona. Por defecto
// Madrid, nunca la del navegador.
export const computeAbsenceMinutes = ({ startIso, endIso, workhours, scheduleHistory, isHourly, timeZone = DEFAULT_TIME_ZONE }) => {
  const startTs = new Date(startIso).getTime();
  const endTs = new Date(endIso).getTime();
  if (!Number.isFinite(startTs) || !Number.isFinite(endTs) || endTs <= startTs) {
    return { minutes: 0, mode: 'sin-jornada' };
  }

  // Índice del workhours ACTUAL (fallback cuando no hay versión histórica
  // para una fecha concreta — típico para fechas anteriores al primer
  // ScheduleVersion del trabajador).
  const currentByDow = new Map();
  for (const s of (Array.isArray(workhours) ? workhours : [])) {
    if (s && s.dayOfWeek != null) currentByDow.set(Number(s.dayOfWeek), s);
  }
  const hasCurrentWorkhours = currentByDow.size > 0;
  const hasHistory = Array.isArray(scheduleHistory) && scheduleHistory.length > 0;

  // Devuelve el slot del `dow` en la fecha dada, resolviendo primero contra
  // el histórico (si lo hay) y cayendo al workhours actual.
  const resolveSlot = (dayKey, dow) => {
    if (hasHistory) {
      const version = findActiveEntry(scheduleHistory, dayKey);
      if (version && Array.isArray(version.assignSlots)) {
        const slot = version.assignSlots.find((s) => Number(s.dayOfWeek) === dow);
        if (slot) return slot;
        // Si la versión NO cubre ese dow (descanso en esa versión),
        // devolvemos null sin caer al actual — esa versión define
        // exhaustivamente la jornada para su rango de validez.
        return null;
      }
    }
    return currentByDow.get(dow) || null;
  };

  let total = 0;
  // Iterar día a día (días del TRABAJADOR) desde el día del START hasta el
  // día del END.
  const days = dayKeysBetween(dayKeyInZone(startTs, timeZone), dayKeyInZone(endTs, timeZone));
  for (const dayKey of days) {
    const dow = jsDowToBackend(dowOfDayKey(dayKey));
    const slot = resolveSlot(dayKey, dow);
    const intervals = slotIntervalsForDay(slot, dayKey, timeZone);
    if (intervals.length > 0) {
      if (isHourly) {
        // Intersección estricta con el rango exacto del par START-END.
        total += intersectMinutes(intervals, startTs, endTs);
      } else {
        // Día completo: cuenta la jornada entera del día.
        total += intervals.reduce((sum, [a, b]) => sum + Math.floor((b - a) / 60000), 0);
      }
    }
  }

  // Si el cálculo dio 0 Y no había NI workhours ACTUAL ni HISTORIAL útil,
  // aplicamos fallback. La ausencia "por horas" cae al rango exacto entre
  // timestamps; la de "día completo" queda en 0 con UI bloqueante.
  const hadAnyJornada = hasCurrentWorkhours || hasHistory;
  if (total === 0 && !hadAnyJornada && isHourly) {
    const exactMinutes = Math.max(0, Math.floor((endTs - startTs) / 60000));
    return { minutes: exactMinutes, mode: 'rango-exacto' };
  }

  return {
    minutes: total,
    mode: hadAnyJornada ? 'jornada' : 'sin-jornada',
  };
};

const fmtHM = (mins) => {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  if (r === 0) return `${h}h`;
  return `${h}h ${String(r).padStart(2, '0')}m`;
};

// Día y hora del instante en la zona del trabajador (el día se pinta en UTC
// para que el navegador no lo desplace).
const fmtDate = (iso, tz = DEFAULT_TIME_ZONE) => {
  try {
    const key = dayKeyInZone(iso, tz);
    if (!key) return iso;
    return new Date(`${key}T00:00:00Z`).toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
  } catch { return iso; }
};
const fmtTime = (iso, tz = DEFAULT_TIME_ZONE) => formatHmInZone(iso, tz) || iso;

// `scheduleStatus`: 'ready' | 'loading' | 'error'. Mientras el horario del
// trabajador no está cargado (o si su petición falló) NO se calculan horas:
// antes un fallo se tomaba como "sin jornada configurada" y el modal daba 0h
// (o las horas con el horario equivocado).
const AbsenceApprovalModal = ({ open, event, endEvent, worker, workhours, scheduleHistory, scheduleStatus = 'ready', onConfirm, onClose }) => {
  const scheduleReady = scheduleStatus === 'ready';
  // Zona del trabajador dueño de la ausencia (fila de `worker`; sin datos → Madrid).
  const tz = timeZoneForWorker(worker && Number(worker.id) === Number(event?.workerId) ? worker : null);
  const tzHint = timeZoneLabel(tz);
  const [currentBalanceMinutes, setCurrentBalanceMinutes] = useState(null);
  // Trabajador SIN horario asignado: la bolsa no tiene número. Aquí se
  // guardan sus ajustes manuales (minutos) para mostrarlos aparte; null si
  // el trabajador sí tiene horario.
  const [noScheduleManual, setNoScheduleManual] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isHourly = event?.eventType === EVENT_TYPES.ABSENCE_START;

  // Cargar la bolsa actual del trabajador. "Bolsa actual" = automático +
  // ajustes manuales: el MISMO total que la ficha del trabajador, el chip de
  // Equipo y la app (antes aquí solo se sumaban los ajustes manuales y el
  // número no cuadraba con ninguna otra pantalla). Si el trabajador no es
  // elegible para la bolsa automática o no tenemos sus datos, caemos a los
  // ajustes manuales, que es lo único que existe para él.
  useEffect(() => {
    if (!open || !event?.workerId) {
      setCurrentBalanceMinutes(null);
      setNoScheduleManual(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingBalance(true);
    setError(null);
    setNoScheduleManual(null);
    const target = worker && Number(worker.id) === Number(event.workerId) ? worker : null;
    const p = target && eligibleForBalance(target)
      ? loadWorkerHourBalance(target, { force: true }).then((r) => {
        // Sin horario: total null → no hay "bolsa actual" numérica.
        if (r.noSchedule) {
          if (!cancelled) setNoScheduleManual(r.manual);
          return null;
        }
        return r.total;
      })
      : getHourBalance(event.workerId).then((list) => sumManualOffsets(Array.isArray(list) ? list : []));
    p
      .then((mins) => { if (!cancelled) setCurrentBalanceMinutes(mins); })
      .catch(() => { if (!cancelled) setCurrentBalanceMinutes(0); })
      .finally(() => { if (!cancelled) setLoadingBalance(false); });
    return () => { cancelled = true; };
  }, [open, event?.workerId, worker]);

  // Minutos de ausencia y modo de cálculo. `mode` puede ser:
  //   · 'jornada'      → calculado con los slots del trabajador (caso ideal).
  //   · 'rango-exacto' → fallback para "por horas" sin jornada (tiempo real
  //                      entre los dos timestamps).
  //   · 'sin-jornada'  → "día completo" sin jornada — no hay cálculo posible,
  //                      la UI bloquea el botón rojo.
  const { absenceMinutes, calcMode } = useMemo(() => {
    if (!event || !endEvent) return { absenceMinutes: 0, calcMode: 'sin-jornada' };
    if (!scheduleReady) return { absenceMinutes: 0, calcMode: 'sin-horario-cargado' };
    const r = computeAbsenceMinutes({
      startIso: event.timestamp,
      endIso: endEvent.timestamp,
      workhours,
      scheduleHistory,
      isHourly,
      timeZone: tz,
    });
    return { absenceMinutes: r.minutes, calcMode: r.mode };
  }, [event, endEvent, workhours, scheduleHistory, isHourly, tz, scheduleReady]);

  // Conteo de días cubiertos por el periodo (informativo), en días del trabajador.
  const dayCount = useMemo(() => {
    if (!event || !endEvent) return 0;
    const n = dayKeysBetween(dayKeyInZone(event.timestamp, tz), dayKeyInZone(endEvent.timestamp, tz)).length;
    return Math.max(1, n);
  }, [event, endEvent, tz]);

  if (!open || !event) return null;

  // El trabajador "tiene jornada" cuando hay slots activos hoy (workhours)
  // O hay al menos una versión en el historial — cualquiera basta para que
  // el cálculo dé un resultado no nulo.
  // Sin horario cargado no se afirma "no tiene jornada" (no lo sabemos).
  const hasWorkhours = !scheduleReady || (Array.isArray(workhours) && workhours.length > 0)
    || (Array.isArray(scheduleHistory) && scheduleHistory.length > 0);
  const balanceLoaded = currentBalanceMinutes != null && !loadingBalance;
  const balanceIfRecoverable = balanceLoaded ? currentBalanceMinutes - absenceMinutes : null;

  const onClick = async (recoverable) => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(recoverable, absenceMinutes);
      // La aprobación cambia la bolsa (día sin jornada esperada y, si es
      // recuperable, un ajuste manual): invalidamos el total cacheado.
      invalidateWorkerHourBalance(event?.workerId);
    } catch (err) {
      setError(err?.message || 'No se pudo guardar.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-1.5">
              {isHourly ? <Clock size={12} /> : <UserMinus size={12} />}
              Aprobar ausencia justificada
            </div>
            <h2 className="font-display text-lg font-bold text-ink truncate">
              {worker?.name || `Trabajador #${event.workerId}`}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Resumen del periodo */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-1.5">
              <CalIcon size={11} /> Periodo solicitado
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-[10px] text-ink-muted uppercase tracking-wider">Desde</div>
                <div className="font-semibold text-ink capitalize">{fmtDate(event.timestamp, tz)}</div>
                {isHourly && (
                  <div className="text-xs text-ink-muted tabular-nums">{fmtTime(event.timestamp, tz)}</div>
                )}
              </div>
              <div>
                <div className="text-[10px] text-ink-muted uppercase tracking-wider">Hasta</div>
                <div className="font-semibold text-ink capitalize">
                  {endEvent ? fmtDate(endEvent.timestamp, tz) : <span className="text-warning">— sin END registrado</span>}
                </div>
                {isHourly && endEvent && (
                  <div className="text-xs text-ink-muted tabular-nums">{fmtTime(endEvent.timestamp, tz)}</div>
                )}
              </div>
            </div>
            <div className="text-xs text-ink-muted">
              <b className="text-ink">{dayCount}</b> día{dayCount === 1 ? '' : 's'} cubierto{dayCount === 1 ? '' : 's'} ·{' '}
              <b className="text-ink">{isHourly ? 'Por horas' : 'Día completo'}</b>
              {isHourly && tzHint && <> · {tzHint}</>}
            </div>
          </div>

          {/* Aviso de jornada. Diferenciamos los dos casos problemáticos:
              · sin jornada + día completo → bloqueante (no sabemos cuántas
                horas son un "día completo" para este trabajador).
              · sin jornada + por horas → usamos rango exacto entre timestamps
                como fallback, pero avisamos. */}
          {scheduleStatus === 'loading' && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start gap-2">
              <Loader2 size={14} className="text-primary animate-spin shrink-0 mt-0.5" />
              <div className="text-xs text-ink">Cargando el horario del trabajador… Las horas de la ausencia se calcularán en cuanto llegue.</div>
            </div>
          )}
          {scheduleStatus === 'error' && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-warning shrink-0 mt-0.5" />
              <div className="text-xs text-ink">
                No se pudo cargar el horario del trabajador, así que <b>no se pueden calcular las horas</b> de la
                ausencia. Puedes aprobarla sin descontar de la bolsa o recargar la página y volver a intentarlo.
              </div>
            </div>
          )}
          {!hasWorkhours && !isHourly && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-warning shrink-0 mt-0.5" />
              <div className="text-xs text-ink">
                Este trabajador <b>no tiene jornada configurada</b>. Como la ausencia es de día completo,
                no podemos saber cuántas horas componen su jornada. Configura su horario semanal en la ficha
                del trabajador antes de poder descontar de la bolsa.
              </div>
            </div>
          )}
          {calcMode === 'rango-exacto' && (
            <div className="rounded-lg border border-info/30 bg-blue-50 p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-blue-600 shrink-0 mt-0.5" />
              <div className="text-xs text-ink">
                Este trabajador no tiene jornada configurada. Como la ausencia es <b>por horas</b>,
                usamos el <b>tiempo exacto entre el inicio y el fin</b> de la ausencia para calcular las horas.
                Si quieres mayor precisión, configura su jornada semanal en la ficha.
              </div>
            </div>
          )}

          {/* Horas calculadas */}
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mb-1.5">
              Horas correspondientes a la ausencia
            </div>
            <div className="flex items-baseline gap-2">
              <div className={`font-display text-3xl font-bold tabular-nums ${
                absenceMinutes > 0 ? 'text-primary-darker' : 'text-ink-muted/60'
              }`}>
                {scheduleReady ? fmtHM(absenceMinutes) : '—'}
              </div>
              <div className="text-xs text-ink-muted">
                {calcMode === 'jornada' && (isHourly
                  ? 'según la jornada del trabajador (rango exacto del periodo dentro de su horario)'
                  : `según la jornada del trabajador (${dayCount} jornada${dayCount === 1 ? '' : 's'} laboral${dayCount === 1 ? '' : 'es'})`)}
                {calcMode === 'rango-exacto' && 'tiempo exacto entre inicio y fin (sin jornada configurada)'}
                {calcMode === 'sin-jornada' && 'no se puede calcular sin jornada configurada'}
                {calcMode === 'sin-horario-cargado' && (scheduleStatus === 'loading' ? 'cargando el horario…' : 'no se pudo cargar el horario')}
              </div>
            </div>
            {absenceMinutes === 0 && hasWorkhours && scheduleReady && (
              <div className="text-[11px] text-warning mt-1.5">
                ⚠ El cálculo da 0 minutos. Comprueba que la ausencia no caiga en días de descanso del trabajador
                o fuera de su jornada habitual.
              </div>
            )}
          </div>

          {/* Bolsa actual vs si se descuenta */}
          <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-1.5">
              <Wallet size={11} /> Impacto en la bolsa
            </div>
            {loadingBalance ? (
              <div className="text-xs text-ink-muted inline-flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" /> Cargando saldo actual…
              </div>
            ) : noScheduleManual != null ? (
              // Trabajador sin horario asignado: la bolsa no tiene número, así
              // que no hay comparativa. Solo sus ajustes manuales, si los hay.
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">Bolsa actual</div>
                <div className="font-display text-sm font-bold text-ink-muted mt-0.5">
                  {formatNoScheduleBalance(noScheduleManual)}
                </div>
                <div className="text-[11px] text-ink-muted mt-1">
                  {NO_SCHEDULE_ADMIN_HINT}.
                  {absenceMinutes > 0 && <> Si se descuenta, se registrará un ajuste manual de −{fmtHM(absenceMinutes)}.</>}
                </div>
              </div>
            ) : absenceMinutes > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">Bolsa actual</div>
                  <div className={`font-display text-base font-bold tabular-nums mt-0.5 ${
                    currentBalanceMinutes > 0 ? 'text-success' : currentBalanceMinutes < 0 ? 'text-danger' : 'text-ink-muted'
                  }`}>
                    {formatBalance(currentBalanceMinutes)}
                  </div>
                </div>
                <div className="rounded-lg border-2 border-success/30 bg-success/5 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-success font-semibold">Si NO se descuenta</div>
                  <div className="font-display text-base font-bold text-success tabular-nums mt-0.5">
                    {formatBalance(currentBalanceMinutes)}
                  </div>
                  <div className="text-[10px] text-ink-muted leading-tight mt-0.5">Sin cambios</div>
                </div>
                <div className="rounded-lg border-2 border-danger/30 bg-danger/5 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-danger font-semibold">Si se descuenta</div>
                  <div className="font-display text-base font-bold text-danger tabular-nums mt-0.5">
                    {balanceIfRecoverable != null ? formatBalance(balanceIfRecoverable) : '—'}
                  </div>
                  <div className="text-[10px] text-ink-muted leading-tight mt-0.5 inline-flex items-center gap-0.5">
                    <TrendingDown size={9} /> −{fmtHM(absenceMinutes)}
                  </div>
                </div>
              </div>
            ) : (
              // Si no se puede calcular impacto (0m), solo mostramos la bolsa
              // actual — no tiene sentido la comparativa.
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">Bolsa actual</div>
                <div className={`font-display text-xl font-bold tabular-nums mt-0.5 ${
                  currentBalanceMinutes > 0 ? 'text-success' : currentBalanceMinutes < 0 ? 'text-danger' : 'text-ink-muted'
                }`}>
                  {formatBalance(currentBalanceMinutes)}
                </div>
                <div className="text-[11px] text-ink-muted mt-1">
                  {scheduleReady
                    ? 'Aprobar esta ausencia no modifica la bolsa.'
                    : 'Sin horario no se puede calcular el impacto en la bolsa.'}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="border border-danger/30 bg-danger/5 rounded-lg p-3 flex items-start gap-2 text-xs">
              <AlertTriangle size={13} className="text-danger shrink-0 mt-0.5" />
              <div className="text-ink font-semibold">{error}</div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 shrink-0 space-y-2">
          {/* Si el cálculo da 0m por falta de jornada (caso día completo sin
              jornada), el botón rojo se OCULTA en lugar de mostrarse
              deshabilitado: pulsarlo no tendría efecto y "Descontar 0m"
              confunde más que aclara. Si hay horas reales (incluso por
              fallback de rango exacto), ambos botones se muestran. */}
          <div className={`grid gap-2 ${absenceMinutes > 0 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
            <button
              type="button"
              onClick={() => onClick(false)}
              disabled={submitting || loadingBalance || scheduleStatus === 'loading'}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-bold text-white bg-success hover:bg-emerald-700 transition-colors disabled:opacity-60"
              title="La empresa asume las horas — la bolsa NO se ve afectada."
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {absenceMinutes > 0
                ? 'No descontar de la bolsa'
                : 'Aprobar (la empresa asume la ausencia)'}
            </button>
            {absenceMinutes > 0 && (
              <button
                type="button"
                onClick={() => onClick(true)}
                disabled={submitting || loadingBalance}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-bold text-white bg-danger hover:bg-red-700 transition-colors disabled:opacity-60"
                title={`El trabajador deberá ${fmtHM(absenceMinutes)} a la empresa.`}
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <TrendingDown size={14} />}
                Descontar {fmtHM(absenceMinutes)} de la bolsa
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="w-full text-xs font-semibold text-ink-muted hover:text-ink py-1.5 rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AbsenceApprovalModal;
